import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Phone, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useVoiceCall } from '../context/VoiceCallContext';

interface VoiceCallButtonProps {
  advisorId: string;
  advisorName: string;
  perMinuteRate: number;
  isVoiceAvailable: boolean;
  onCallEnd?: () => void;
}

export default function VoiceCallButton({
  advisorId,
  advisorName,
  perMinuteRate,
  isVoiceAvailable
}: VoiceCallButtonProps) {
  const { user } = useAuth();
  const { startCall, isCallActive } = useVoiceCall();
  const [isProcessing, setIsProcessing] = useState(false);
  const [freeMinutes, setFreeMinutes] = useState(0);

  const fetchFreeMinutes = async () => {
    if (!user) return;
    try {
      const { data: creditsData } = await supabase
        .from('user_credits')
        .select('free_minutes_remaining')
        .eq('user_id', user.id)
        .maybeSingle();
      setFreeMinutes(creditsData?.free_minutes_remaining || 0);
    } catch (err) {
      console.error('Error fetching free minutes:', err);
    }
  };

  const handleStartCallClick = async () => {
    if (isCallActive) {
      return;
    }

    setIsProcessing(true);
    try {
      await startCall(advisorId, advisorName, perMinuteRate);
    } catch (err) {
      console.error('Error starting call:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchFreeMinutes();
    }
  }, [user]);

  if (!isVoiceAvailable) return null;

  return (
    <button
      onClick={handleStartCallClick}
      disabled={isProcessing || isCallActive}
      className="w-full flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
    >
      {isProcessing ? (
        <Loader2 className="animate-spin" />
      ) : (
        <>
          <Phone size={16} className="mr-2" />
          {freeMinutes > 0 ? `Voice Call (${Math.floor(freeMinutes)} free min)` : 'Voice Call'}
        </>
      )}
    </button>
  );
}
